import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../index.css';
import { getBlogs } from '../services/blogService';
import getOptimizedUrl from '../utils/imageUtils';

const colors = [
  '#1244F1', // Blue
  '#FE6416', // Orange
  '#FE9FF5', // Pink
  '#FF0D01', // Red
  '#ffffff', // White
  '#E1F532', // Light Green
];

// blogPosts will be fetched from backend

// Helper function to pick initial post index from URL after blogs loaded
const computeInitialIndex = (blogs) => {
  if (typeof window === 'undefined') return 0;
  const params = new URLSearchParams(window.location.search);
  const blogParam = params.get('blog');
  if (!blogParam) return 0;
  // if numeric index provided
  const asIndex = parseInt(blogParam, 10);
  if (!isNaN(asIndex) && asIndex >= 0 && asIndex < blogs.length) return asIndex;
  // if looks like an ObjectId, find by _id
  if (/^[0-9a-fA-F]{24}$/.test(blogParam)) {
    const idx = blogs.findIndex(b => String(b._id) === blogParam);
    if (idx >= 0) return idx;
  }
  // fallback: try matching slug
  const bySlug = blogs.findIndex(b => b.slug === blogParam);
  if (bySlug >= 0) return bySlug;
  return 0;
};

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    }).format(new Date(iso));
  } catch (e) {
    return '';
  }
};

function NewBlog() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const blogFrameRef = useRef(null);
  const logoCircleRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState('#1244F1');

  const initialLayout = typeof window !== 'undefined'
    ? (() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const frameWidth = vw * 0.55;
        const frameHeight = vh * 0.70;
        return {
          frame: {
            x: Math.max(0, vw - frameWidth),
            y: Math.max(120, vh - frameHeight),
          },
          cover: {
            x: Math.max(24, vw * 0.22),
            y: Math.max(130, vh * 0.20),
          },
          logo: {
            x: Math.max(24, vw * 0.50),
            y: Math.max(36, vh * 0.06),
          },
        };
      })()
    : {
        frame: { x: 0, y: 0 },
        cover: { x: 32, y: 140 },
        logo: { x: 480, y: 90 },
      };

  const [framePosition, setFramePosition] = useState(initialLayout.frame);

  const [logoPosition, setLogoPosition] = useState(initialLayout.logo);

  const [isDraggingFrame, setIsDraggingFrame] = useState(false);
  const [dragOffsetFrame, setDragOffsetFrame] = useState({ x: 0, y: 0 });

  const coverFrameRef = useRef(null);
  const [coverFramePosition, setCoverFramePosition] = useState(initialLayout.cover);
  const [isDraggingCoverFrame, setIsDraggingCoverFrame] = useState(false);
  const [dragOffsetCoverFrame, setDragOffsetCoverFrame] = useState({ x: 0, y: 0 });

  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [dragOffsetLogo, setDragOffsetLogo] = useState({ x: 0, y: 0 });
  const [hasDraggedLogo, setHasDraggedLogo] = useState(false);

  const [eyes, setEyes] = useState([]);
  const inactivityTimeoutRef = useRef(null);
  const eyeIntervalRef = useRef(null);
  let eyeIdCounter = useRef(0);

  const [pencilPosition, setPencilPosition] = useState({ x: 0, y: 0 });
  const [showPencil, setShowPencil] = useState(false);
  const [cursorScale, setCursorScale] = useState(1.2);
  const lastMouseX = useRef(0);
  const lastMouseY = useRef(0);
  const lastMouseTime = useRef(0);
  const cursorResetTimeout = useRef(null);

  const [isGrabbing, setIsGrabbing] = useState(false);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushWidth, setBrushWidth] = useState(2);
  const [isErasing, setIsErasing] = useState(false);


  const [showBlogContentFrame, setShowBlogContentFrame] = useState(true);
  const [showCoverImageFrame, setShowCoverImageFrame] = useState(true);
  const [showNavButtons, setShowNavButtons] = useState(true);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const [showMoreOptions, setShowMoreOptions] = useState(false);
  
  

  const [canvasHistory, setCanvasHistory] = useState([]);
  const [historyPointer, setHistoryPointer] = useState(-1);
  const MAX_HISTORY_STATES = 10;

  const [eyesMode] = useState('hate');
  const persistentEyesIntervalRef = useRef(null);

  const [gameMode, setGameMode] = useState(false);
  const [gunX, setGunX] = useState(typeof window !== 'undefined' ? window.innerWidth / 2 : 400);
  const [bullets, setBullets] = useState([]);
  const bulletIdRef = useRef(0);
  const gameEyesIntervalRef = useRef(null);

  const [initialEyesLeft, setInitialEyesLeft] = useState(10);
  const [gameOver, setGameOver] = useState(false);
  const [killCount, setKillCount] = useState(0);

  const randomizeColor = () => {
    const currentIndex = colors.indexOf(backgroundColor);
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * colors.length);
    } while (newIndex === currentIndex);
    setBackgroundColor(colors[newIndex]);
  };

  // Fetch blogs from backend on mount
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getBlogs();
        if (!mounted) return;
        // Normalize fields: ensure content is array and use dedicated previewSnippet
        const normalized = (data || []).map(b => ({
          ...b,
          content: Array.isArray(b.content) ? b.content : (typeof b.content === 'string' ? b.content.split(/\n\s*\n/).map(s=>s.trim()).filter(Boolean) : []),
          date: fmtDate(b.createdAt),
          previewSnippet: b.previewSnippet || b.intro || b.excerpt || '',
          readingTime: b.readingTime !== undefined ? Number(b.readingTime) : 5,
        }));
        setBlogPosts(normalized);
        setLoadingBlogs(false);
        // set initial index based on URL param
        const initial = computeInitialIndex(normalized);
        setCurrentPostIndex(initial);
      } catch (err) {
        console.error('Failed to load blogs', err);
        setBlogPosts([]);
        setLoadingBlogs(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const addEye = useCallback(() => {
    if (typeof window === 'undefined') return;
    const padding = 50;
    const x = Math.random() * (window.innerWidth - padding * 2) + padding;
    const y = Math.random() * (window.innerHeight - padding * 2) + padding;
    const rotation = Math.random() * 360;
    setEyes((prevEyes) => [...prevEyes, { id: eyeIdCounter.current++, x, y, rotation }]);
  }, []);

  useEffect(() => {
    if (eyesMode === 'like') {
      addEye();
      persistentEyesIntervalRef.current = window.setInterval(addEye, 15000);
    } else {
      if (persistentEyesIntervalRef.current) {
        clearInterval(persistentEyesIntervalRef.current);
        persistentEyesIntervalRef.current = null;
      }
      setEyes([]);
    }
    return () => {
      if (persistentEyesIntervalRef.current) {
        clearInterval(persistentEyesIntervalRef.current);
        persistentEyesIntervalRef.current = null;
      }
    };
  }, [eyesMode, addEye]);

  const resetInactivityTimer = useCallback(() => {
    if (eyesMode !== 'hate') return;
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (eyeIntervalRef.current) {
      clearInterval(eyeIntervalRef.current);
      setEyes([]);
    }
    inactivityTimeoutRef.current = window.setTimeout(() => {
      addEye();
      eyeIntervalRef.current = window.setInterval(addEye, 15000);
    }, 40000);
  }, [addEye, eyesMode]);

  const saveCanvasState = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL();
      setCanvasHistory(prevHistory => {
        const newHistory = prevHistory.slice(0, historyPointer + 1);
        newHistory.push(dataUrl);
        const cappedHistory = newHistory.length > MAX_HISTORY_STATES
          ? newHistory.slice(newHistory.length - MAX_HISTORY_STATES)
          : newHistory;
        setHistoryPointer(cappedHistory.length - 1);
        return cappedHistory;
      });
    }
  }, [historyPointer, MAX_HISTORY_STATES]);

  const handleClearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      saveCanvasState();
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const original = ctx.globalCompositeOperation;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = original;
      }
    }
  }, [saveCanvasState]);

  const handleSaveDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const temp = document.createElement('canvas');
      temp.width = canvas.width;
      temp.height = canvas.height;
      const tctx = temp.getContext('2d');
      if (!tctx) return;
      tctx.fillStyle = backgroundColor || '#ffffff';
      tctx.fillRect(0, 0, temp.width, temp.height);
      tctx.drawImage(canvas, 0, 0);
      const dataUrl = temp.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'masterpiece.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [backgroundColor]);

  

  

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setLastPosition({ x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushWidth;
    ctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';

    ctx.beginPath();
    ctx.moveTo(lastPosition.x, lastPosition.y);
    ctx.lineTo(x, y);
    ctx.stroke();

    setLastPosition({ x, y });
  };

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      saveCanvasState();
    }
    setIsDrawing(false);
  }, [isDrawing, saveCanvasState]);

  

  const resizeCanvasAndRestore = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return;

    const currentContentDataUrl = canvas.toDataURL();

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = currentContentDataUrl;
    }
  }, []);

  const isInitialMount = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return;

    resizeCanvasAndRestore();
    window.addEventListener('resize', resizeCanvasAndRestore);

    if (isInitialMount.current) {
      if (canvasHistory.length === 0 && historyPointer === -1) {
        saveCanvasState();
      }
      isInitialMount.current = false;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    script.async = true;
    document.body.appendChild(script);

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    activityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    resetInactivityTimer();

    return () => {
      window.removeEventListener('resize', resizeCanvasAndRestore);
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (eyeIntervalRef.current) {
        clearInterval(eyeIntervalRef.current);
      }
    };
  }, [resizeCanvasAndRestore, saveCanvasState, resetInactivityTimer, canvasHistory.length, historyPointer]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = brushColor;
      ctx.lineWidth = brushWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
    }
  }, [brushWidth, brushColor, isErasing]);

  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      const currentTime = performance.now();

      setPencilPosition({ x: currentX, y: currentY });
      setShowPencil(true);

      const dx = currentX - lastMouseX.current;
      const dy = currentY - lastMouseY.current;
      const dt = currentTime - lastMouseTime.current;

      if (dt > 0) {
        const distance = Math.sqrt(dx * dx + dy * dy);
        const speed = distance / dt;

        const maxSpeed = 2;
        const minSpeed = 0.1;
        const calculatedScale = 1 + Math.min(1, Math.max(0, (speed - minSpeed) / (maxSpeed - minSpeed)));

        setCursorScale(calculatedScale);

        if (cursorResetTimeout.current) {
          clearTimeout(cursorResetTimeout.current);
        }
        cursorResetTimeout.current = window.setTimeout(() => {
          setCursorScale(1.2);
        }, 100);
      }

      lastMouseX.current = currentX;
      lastMouseY.current = currentY;
      lastMouseTime.current = currentTime;
    }
    draw(e);
  };

  const handleCanvasMouseLeave = () => {
    setShowPencil(false);
    stopDrawing();
    setCursorScale(1.2);
    if (cursorResetTimeout.current) {
      clearTimeout(cursorResetTimeout.current);
    }
  };

  const updateUrlForPost = (index) => {
    if (typeof window !== 'undefined' && window.location.protocol !== 'blob:') {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('blog', index.toString());
      window.history.pushState({ blogIndex: index }, '', newUrl.toString());
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // initialize history with initial index (0 for now)
      window.history.replaceState({ blogIndex: 0 }, '');

      const handlePopState = (event) => {
        if (window.location.protocol !== 'blob:') {
          const newIndex = event.state?.blogIndex ?? 0;
          setCurrentPostIndex(newIndex);
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  const navigateToPost = (index) => {
    setCurrentPostIndex(index);
    updateUrlForPost(index);
  };

  const nextPost = () => {
    if (blogPosts.length === 0) return;
    navigateToPost((currentPostIndex + 1) % blogPosts.length);
  };

  const prevPost = () => {
    if (blogPosts.length === 0) return;
    navigateToPost((currentPostIndex - 1 + blogPosts.length) % blogPosts.length);
  };

  const startDraggingFrame = (e) => {
    if (!blogFrameRef.current) return;

    setIsDraggingFrame(true);
    setIsGrabbing(true);
    setPencilPosition({ x: e.clientX, y: e.clientY });
    const rect = blogFrameRef.current.getBoundingClientRect();
    setDragOffsetFrame({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const dragFrame = (e) => {
    if (!isDraggingFrame) return;

    setPencilPosition({ x: e.clientX, y: e.clientY });

    setFramePosition({
      x: e.clientX - dragOffsetFrame.x,
      y: e.clientY - dragOffsetFrame.y,
    });
  };

  const stopDraggingFrame = () => {
    setIsDraggingFrame(false);
    setIsGrabbing(false);
  };

  const startDraggingLogo = (e) => {
    if (!logoCircleRef.current) return;

    setIsDraggingLogo(true);
    setIsGrabbing(true);
    setPencilPosition({ x: e.clientX, y: e.clientY });
    const rect = logoCircleRef.current.getBoundingClientRect();
    setDragOffsetLogo({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setHasDraggedLogo(false);
  };

  const dragLogo = (e) => {
    if (!isDraggingLogo) return;

    setPencilPosition({ x: e.clientX, y: e.clientY });

    setLogoPosition({
      x: e.clientX - dragOffsetLogo.x,
      y: e.clientY - dragOffsetLogo.y,
    });
    setHasDraggedLogo(true);
  };

  const stopDraggingLogo = () => {
    setIsDraggingLogo(false);
    setIsGrabbing(false);
  };

  const handleLogoClick = () => {
    if (!hasDraggedLogo) {
      window.open('https://www.instagram.com/nufab.studio?igsh=dTZyM3Frem1hb3Y5&utm_source=qr', '_blank');
    }
    setHasDraggedLogo(false);
  };

  const handleDeleteBlogClick = () => {
    setShowDeleteConfirmationModal(true);
  };

  const confirmDeleteBlog = () => {
    setShowBlogContentFrame(false);
    setShowNavButtons(false);
    setShowMoreOptions(false);
    setShowDeleteConfirmationModal(false);
  };

  const abortDeleteBlog = () => {
    setShowDeleteConfirmationModal(false);
  };

  const handleDeleteCoverFrame = (e) => {
    e.stopPropagation();
    setShowCoverImageFrame(false);
  };

  

  const handleLogoMouseEnter = useCallback(() => {
    setIsGrabbing(true);
  }, []);

  const handleLogoMouseLeave = useCallback(() => {
    stopDraggingLogo();
    setIsGrabbing(false);
  }, []);

  const currentPost = blogPosts[currentPostIndex] || null;
  const currentPostCoverImage =
    currentPost?.images?.[0] ||
    currentPost?.imageUrl ||
    currentPost?.coverImage ||
    currentPost?.image ||
    '';

  const startDraggingCoverFrame = (e) => {
    if (!coverFrameRef.current) return;
    setIsDraggingCoverFrame(true);
    setIsGrabbing(true);
    setPencilPosition({ x: e.clientX, y: e.clientY });
    const rect = coverFrameRef.current.getBoundingClientRect();
    setDragOffsetCoverFrame({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const dragCoverFrame = (e) => {
    if (!isDraggingCoverFrame) return;
    setPencilPosition({ x: e.clientX, y: e.clientY });
    setCoverFramePosition({
      x: e.clientX - dragOffsetCoverFrame.x,
      y: e.clientY - dragOffsetCoverFrame.y,
    });
  };

  const stopDraggingCoverFrame = () => {
    setIsDraggingCoverFrame(false);
    setIsGrabbing(false);
  };

  const handleCoverFrameMouseEnter = () => {
    setShowPencil(true);
  };

  const handleCoverFrameMouseMove = (e) => {
    setPencilPosition({ x: e.clientX, y: e.clientY });
    setShowPencil(true);
    dragCoverFrame(e);
  };

  const handleCoverFrameMouseLeave = () => {
    stopDraggingCoverFrame();
    setShowPencil(false);
  };

  const handleBackToBlogs = () => {
    if (window.history.length > 1) {
      navigate('/blogs', { replace: true });
      return;
    }
    navigate('/blogs');
  };

  const spawnRandomEye = useCallback(() => {
    if (typeof window === 'undefined') return;
    const padding = 50;
    const x = Math.random() * (window.innerWidth - padding * 2) + padding;
    const y = Math.random() * (window.innerHeight - 200 - padding * 2) + padding;
    const rotation = Math.random() * 360;
    setEyes(prevEyes => [...prevEyes, { id: eyeIdCounter.current++, x, y, rotation }]);
  }, []);

  useEffect(() => {
    if (gameMode) {
      setEyes([]);
      setBullets([]);
      setInitialEyesLeft(10);
      setTimeout(() => {
        for (let i = 0; i < 10; i++) spawnRandomEye();
      }, 0);
    }
  }, [gameMode, spawnRandomEye]);

  useEffect(() => {
    if (gameMode && typeof window !== 'undefined') {
      if (gameEyesIntervalRef.current) {
        clearInterval(gameEyesIntervalRef.current);
        gameEyesIntervalRef.current = null;
      }

      const isMobile = window.innerWidth < 768;
      const spawnInterval = isMobile ? 10000 : 2000;

      if (isMobile) {
        setTimeout(() => {
          spawnRandomEye();
          gameEyesIntervalRef.current = window.setInterval(spawnRandomEye, spawnInterval);
        }, 20000);
      } else {
        gameEyesIntervalRef.current = window.setInterval(spawnRandomEye, spawnInterval);
      }
    } else {
      if (gameEyesIntervalRef.current) {
        clearInterval(gameEyesIntervalRef.current);
        gameEyesIntervalRef.current = null;
      }
    }
    return () => {
      if (gameEyesIntervalRef.current) {
        clearInterval(gameEyesIntervalRef.current);
        gameEyesIntervalRef.current = null;
      }
    };
  }, [gameMode, spawnRandomEye]);

  useEffect(() => {
    if (!gameMode || gameOver || typeof window === 'undefined') return;
    if (bullets.length === 0) return;
    const interval = setInterval(() => {
      setBullets(bullets => bullets.map(b => {
        if (b.dir === undefined) b.dir = true;
        if (b.dir) {
          if (b.y - 20 <= 0) {
            return { ...b, y: 0, dir: false };
          }
          return { ...b, y: b.y - 20 };
        } else {
          return { ...b, y: b.y + 20 };
        }
      }).filter(b => b.y < window.innerHeight));
      setEyes(eyes => {
        let killed = 0;
        const filtered = eyes.filter(eye => {
          const hit = bullets.some(b => {
            const dx = b.x - eye.x;
            const dy = b.y - eye.y;
            return b.dir && Math.sqrt(dx * dx + dy * dy) < 50;
          });
          if (hit) killed++;
          return !hit;
        });
        if (killed > 0 && initialEyesLeft > 0) setInitialEyesLeft(n => Math.max(0, n - killed));
        if (killed > 0) setKillCount(c => c + killed);
        return filtered;
      });
      setBullets(bullets => {
        if (gameOver) return bullets;
        const hit = bullets.some(b => {
          if (b.dir === false && b.y >= window.innerHeight - 60) {
            return Math.abs(b.x - gunX) < 30;
          }
          return false;
        });
        if (hit) setGameOver(true);
        return bullets;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [gameMode, bullets, initialEyesLeft, gunX, gameOver, killCount]);

  const [isShooting, setIsShooting] = useState(false);
  useEffect(() => {
    if (!gameMode) return;
    if (!isShooting) return;
    const timeout = setTimeout(() => setIsShooting(false), 100);
    return () => clearTimeout(timeout);
  }, [isShooting, gameMode]);

  useEffect(() => {
    if (!gameMode || typeof window === 'undefined') return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setGunX(x => Math.max(40, x - 40));
      } else if (e.key === 'ArrowRight') {
        setGunX(x => Math.min(window.innerWidth - 40, x + 40));
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Shift') {
        setIsShooting(true);
        setBullets(bullets => [
          ...bullets,
          { x: gunX, y: window.innerHeight - 60, id: bulletIdRef.current++, type: 'asterisk' }
        ]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameMode, gunX]);

  const handleEyesGameToggle = () => {
    if (!gameMode && typeof window !== 'undefined') {
      setGameMode(true);
      setEyes([]);
      setBullets([]);
      setGunX(window.innerWidth / 2);
      setKillCount(0);
      setGameOver(false);
      setInitialEyesLeft(10);
      setShowMoreOptions(false);
    }
  };

  const handleExitGame = () => {
    window.location.reload();
  };

  // While blogs are loading or if none found, show placeholder to avoid null access
  if (loadingBlogs) {
    return <div className="w-full py-20 text-center">Loading blogs...</div>;
  }
  if (!loadingBlogs && !currentPost) {
    return <div className="w-full py-20 text-center">No blog posts found.</div>;
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor }}>
      <style>
        {`
          @font-face {
            font-family: 'dotmatri';
            src: url('/DOTMATRI.TTF') format('truetype');
            font-weight: normal;
            font-style: normal;
          }

          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: white;
            border-radius: 0px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: black;
            border-radius: 0px;
            border: 1px solid black;
          }

          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: black white;
          }
        `}
      </style>

      {/* Drawing Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 z-0 h-screen w-screen"
        onMouseDown={startDrawing}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={stopDrawing}
        onMouseLeave={handleCanvasMouseLeave}
        style={{ pointerEvents: 'auto', cursor: 'none' }}
      />

      {/* Custom Cursor */}
      {!gameMode && (
        <>
          {isGrabbing && (
            <img
              src="https://raw.githubusercontent.com/Raeskaa/studionufab/b14b17e2dce2b5fff264d91d41c2e787bec30673/Grab%20Cursor.svg"
              alt="Grab Cursor"
              className="absolute z-50"
              style={{
                top: pencilPosition.y,
                left: pencilPosition.x,
                pointerEvents: 'none',
                transform: 'translate(-50%, -50%)',
                width: '32px',
                height: '32px',
              }}
            />
          )}

          {!isGrabbing && showPencil && (
            <img
              src="https://raw.githubusercontent.com/Raeskaa/studionufab/59d263299b8c3990bd561abbd995c9f13409b6ac/Cursor%20for%20Blog.svg"
              alt="Pencil Cursor"
              className="absolute z-50"
              style={{
                top: pencilPosition.y,
                left: pencilPosition.x,
                pointerEvents: 'none',
                transform: `translate(-50%, -50%) scale(${cursorScale})`,
                width: '40px',
                height: '40px',
              }}
            />
          )}
        </>
      )}

      {/* Eyes */}
      {!gameMode && eyes.map((eye) => (
        <div
          key={eye.id}
          className="absolute z-40"
          style={{
            top: eye.y,
            left: eye.x,
            pointerEvents: 'none',
          }}
        >
          <img
            src="https://raw.githubusercontent.com/Raeskaa/studionufab/ec81588c796d013bbd3fcb4d473985d8e0f87a8b/Nufab%20Eye%20White.svg"
            alt="Eye Icon"
            style={{
              width: '80.6px',
              height: '80.6px',
              transform: `rotate(${eye.rotation}deg)`,
            }}
          />
        </div>
      ))}

      {/* Logo Circle */}
      {!gameMode && (
        <div
          ref={logoCircleRef}
          className="hidden md:flex absolute rounded-full z-[70] items-center justify-center"
          style={{
            top: logoPosition.y,
            left: logoPosition.x,
            width: '180px',
            height: '180px',
            backgroundColor: '#ffffff',
            cursor: 'none',
          }}
          onMouseDown={startDraggingLogo}
          onMouseMove={(e) => {
            dragLogo(e);
            setPencilPosition({ x: e.clientX, y: e.clientY });
          }}
          onMouseUp={stopDraggingLogo}
          onClick={handleLogoClick}
          onMouseEnter={handleLogoMouseEnter}
          onMouseLeave={handleLogoMouseLeave}
          onDragStart={e => e.preventDefault()}
        >
          <img
            src="https://raw.githubusercontent.com/Raeskaa/studionufab/ffdd3e0158bd7e83bfb51a14f710792b29061d90/Group.svg"
            alt="Decorative Icon"
            className="w-2/3 h-2/3"
            draggable={false}
          />
        </div>
      )}

      {/* Back Button */}
      {!gameMode && (
        <div className="fixed top-5 left-5 z-40">
          <button
            onClick={handleBackToBlogs}
            className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-3 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
            style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
          >
            <span className="text-black leading-none" style={{ fontSize: '0.875rem' }}>
              Back
            </span>
          </button>
        </div>
      )}

      {/* Navigation Buttons */}
      {showNavButtons && !gameMode && (
        <div className="absolute flex gap-2 z-20" style={{
          bottom: 'calc(70vh + 14px)',
          right: '2vw',
        }}>
          <button
            onClick={prevPost}
            className="w-8 h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
            style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
          >
            <ChevronLeft size={16} className="text-black" />
          </button>
          <button
            onClick={nextPost}
            className="w-8 h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
            style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
          >
            <ChevronRight size={16} className="text-black" />
          </button>
          <button
            onClick={randomizeColor}
            className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors ml-2 px-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
            style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
          >
            <span className="text-black leading-none" style={{ fontSize: '0.875rem' }}>I'm bored</span>
          </button>
          <button
            onClick={handleEyesGameToggle}
            className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors ml-2 px-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
            style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
          >
            <span className="text-black leading-none" style={{ fontSize: '0.875rem' }}>I'm still bored</span>
          </button>
        </div>
      )}

      {/* Buttons after deletion */}
      {!showBlogContentFrame && !gameMode && (
        <>
          <div className="fixed bottom-5 right-5 flex gap-4 z-40">
            <button
              onClick={randomizeColor}
              className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
            >
              <span className="text-black" style={{ fontSize: '0.875rem' }}>I'm bored</span>
            </button>
            <button
              onClick={handleEyesGameToggle}
              className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
            >
              <span className="text-black" style={{ fontSize: '0.875rem' }}>I'm still bored</span>
            </button>
          </div>

          <div className="fixed top-5 right-5 z-40">
            <button
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              className="h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
            >
              <span className="text-black" style={{ fontSize: '0.875rem' }}>More</span>
            </button>
            {showMoreOptions && (
              <div
                className="absolute top-full right-0 mt-2 flex flex-col gap-2 bg-white border border-black p-2 shadow-lg transition-all duration-300"
                style={{ transformOrigin: 'top right' }}
              >
                <div style={{ minWidth: 150 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ fontFamily: 'Courier Prime, monospace' }}>Brush Size:</p>
                    <button onClick={() => setShowMoreOptions(false)} style={{padding:'0px 6px', border: '1px solid black', background: 'transparent' }}>×</button>
                  </div>
                  <div className="mt-2" style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => { setBrushWidth(1); setIsErasing(false); }}
                      style={{
                        border: `${brushWidth === 1 ? 3 : 2}px solid black`,
                        padding: '6px 10px',
                        background: 'white',
                        cursor: 'pointer',
                        fontFamily: 'Courier Prime, monospace' 
                      }}
                    >Thin</button>
                    <button
                      type="button"
                      onClick={() => { setBrushWidth(3); setIsErasing(false); }}
                      style={{
                        border: `${brushWidth === 3 ? 3 : 2}px solid black`,
                        padding: '6px 10px',
                        background: 'white',
                        cursor: 'pointer',
                        fontFamily: 'Courier Prime, monospace' 
                      }}
                    >Medium</button>
                    <button
                      type="button"
                      onClick={() => { setBrushWidth(6); setIsErasing(false); }}
                      style={{
                        border: `${brushWidth === 6 ? 3 : 2}px solid black`,
                        padding: '6px 10px',
                        background: 'white',
                        cursor: 'pointer',
                        fontFamily: 'Courier Prime, monospace' 
                      }}
                    >Thick</button>
                  </div>

                  <div className="mt-3">
                    <div style={{ fontFamily: 'Courier Prime, monospace' }}>Brush Color:</div>
                    <input
                      type="color"
                      value={brushColor}
                      onChange={(e) => { setBrushColor(e.target.value); setIsErasing(false); }}
                      style={{ width: '100%', height: 30, border: '1px solid #000', marginTop: 6 }}
                    />
                  </div>

                  <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button style={{fontFamily: 'Courier Prime, monospace' }} onClick={() => setIsErasing(!isErasing)} className="border px-2 py-1">{isErasing ? 'Drawing' : 'Erase'}</button>
                    <button style={{fontFamily: 'Courier Prime, monospace' }} onClick={() => { handleClearCanvas(); setShowMoreOptions(false); }} className="border px-2 py-1">Clear</button>
                    <button style={{fontFamily: 'Courier Prime, monospace' }} onClick={() => { handleSaveDrawing(); setShowMoreOptions(false); }} className="border px-2 py-1">Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Blog Content Frame */}
      {!gameMode && showBlogContentFrame && currentPost && (
        <article
          ref={blogFrameRef}
          className="absolute bg-white border-2 border-black z-30 p-8"
          style={{
            top: framePosition.y,
            left: framePosition.x,
            width: '55vw',
            height: '70vh',
            cursor: 'none',
            position: 'absolute',
          }}
          onMouseDown={startDraggingFrame}
          onMouseMove={(e) => {
            dragFrame(e);
            setPencilPosition({ x: e.clientX, y: e.clientY });
            if (!(e.target).closest('button')) {
              setIsGrabbing(true);
            } else {
              setIsGrabbing(false);
            }
          }}
          onMouseUp={stopDraggingFrame}
          onMouseEnter={(e) => {
            if (!(e.target).closest('button')) {
              setIsGrabbing(true);
            }
          }}
          onMouseLeave={() => {
            stopDraggingFrame();
            setIsGrabbing(false);
          }}
        >
          {/* Delete Button */}
          <div className="absolute top-4 left-4 flex gap-2 z-20">
            <button
              onClick={handleDeleteBlogClick}
              className="h-8 bg-white border-solid border-2 border-black hover:bg-gray-100 flex items-center justify-center transition-colors px-3 active:scale-95 hover:shadow-lg focus:outline-none"
              style={{ 
                fontFamily: 'Courier Prime, Courier, monospace', 
                fontWeight: 400,
                border: '1px solid black',
                outline: 'none',
                boxShadow: 'none'
              }}
            >
              <span className="text-black" style={{ fontSize: '0.875rem', fontWeight: 400 }}>dlt</span>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto h-full w-full pt-16 pb-8 md:pr-4 custom-scrollbar">
            <h1
              className="text-black mb-0 leading-tight text-2xl md:text-4xl lg:text-5xl uppercase"
              style={{
                fontFamily: 'dotmatri, serif',
                fontWeight: '400'
              }}
            >
              {currentPost.title}
            </h1>

            {currentPostIndex !== 1 && currentPost.subtitle && (
              Array.isArray(currentPost.subtitle) ? (
                <div className="mb-8">
                  {currentPost.subtitle.map((para, idx) => (
                    <h2
                      key={idx}
                      className="mb-4 leading-relaxed text-base"
                      style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, color: '#000', lineHeight: 1.3 }}
                    >
                      {para}
                    </h2>
                  ))}
                </div>
              ) : (
                <h2
                  className="mb-4 leading-relaxed text-base"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, color: '#000', lineHeight: 1.3 }}
                >
                  {currentPost.subtitle}
                </h2>
              )
            )}

            <p
              className={`text-base mb-10 ${(currentPostIndex === 0 || currentPostIndex === 1 || currentPostIndex === 2) ? 'text-gray-500 text-sm md:text-base lg:text-lg leading-snug' : ''}`}
              style={(currentPostIndex === 0 || currentPostIndex === 1 || currentPostIndex === 2) ? {
                fontFamily: 'Courier Prime, Courier, monospace',
                fontWeight: 400,
                color: '#6B7280',
                lineHeight: 1.3,
              } : {
                fontFamily: 'Courier Prime, monospace',
                fontWeight: 400,
              }}
            >
              {currentPost.date} {currentPost.readingTime ? '• ' + currentPost.readingTime + ' min' : '• 5 min'}
            </p>

            <div className="space-y-6">
              {currentPost.content.map((paragraph, index) => {
                if (paragraph.length > 50) {
                  return (
                    <p
                      key={index}
                      className="text-black leading-relaxed text-sm md:text-base lg:text-lg"
                      style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                    >
                      {paragraph}
                    </p>
                  );
                } else if (paragraph.length > 0) {
                  return (
                    <h3
                      key={index}
                      className="font-bold text-black mt-8 mb-4 text-base md:text-lg lg:text-xl"
                      style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 700 }}
                    >
                      {paragraph}
                    </h3>
                  );
                }
                return null;
              })}
            </div>

            {currentPostIndex === 1 && currentPost.subtitle && (
              <div className="mt-10">
                {Array.isArray(currentPost.subtitle)
                  ? currentPost.subtitle.map((para, idx) => (
                    <p
                      key={idx}
                      className="text-black leading-relaxed text-sm md:text-base lg:text-lg mb-6"
                      style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                    >
                      {para}
                    </p>
                  ))
                  : currentPost.subtitle.split('\n\n').map((para, idx) => (
                    <p
                      key={idx}
                      className="text-black leading-relaxed text-sm md:text-base lg:text-lg mb-6"
                      style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                    >
                      {para}
                    </p>
                  ))}
              </div>
            )}
          </div>
        </article>
      )}

      {/* Cover Image Frame (Desktop) */}
      {!gameMode && showCoverImageFrame && currentPostCoverImage && (
        <div
          ref={coverFrameRef}
          className="hidden md:block absolute bg-white border-2 border-black z-20 p-2"
          style={{
            top: coverFramePosition.y,
            left: coverFramePosition.x,
            width: '24vw',
            minWidth: '220px',
            maxWidth: '360px',
            aspectRatio: '3 / 4',
            cursor: 'none',
          }}
          onMouseDown={startDraggingCoverFrame}
          onMouseEnter={handleCoverFrameMouseEnter}
          onMouseMove={handleCoverFrameMouseMove}
          onMouseUp={stopDraggingCoverFrame}
          onMouseLeave={handleCoverFrameMouseLeave}
        >
          <div className="absolute top-2 left-2 z-30">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleDeleteCoverFrame}
              className="h-8 bg-white border-solid border-2 border-black hover:bg-gray-100 flex items-center justify-center transition-colors px-3 active:scale-95 hover:shadow-lg focus:outline-none"
              style={{
                fontFamily: 'Courier Prime, Courier, monospace',
                fontWeight: 400,
                border: '1px solid black',
                outline: 'none',
                boxShadow: 'none'
              }}
            >
              <span className="text-black" style={{ fontSize: '0.875rem', fontWeight: 400 }}>dlt</span>
            </button>
          </div>
          <img
            src={getOptimizedUrl(currentPostCoverImage || '', 1200)}
            alt={`${currentPost.title || 'Blog'} cover`}
            className="w-full h-full object-cover border border-black"
            draggable={false}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-none shadow-lg text-center border-2 border-black">
            <h3 className="text-lg font-bold mb-4 text-black" style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}>
              Delete the blog frame?
            </h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={confirmDeleteBlog}
                className="h-8 bg-white hover:bg-gray-100 border border-black px-4 transition-colors active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
                style={{ fontFamily: 'Courier Prime, Courier, monospace',  border:'1px solid black', fontWeight: 400 }}
              >
                Yes
              </button>
              <button
                onClick={abortDeleteBlog}
                className="h-8 bg-white hover:bg-gray-100 border border-black px-4 transition-colors active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
                style={{ fontFamily: 'Courier Prime, Courier, monospace', border:'1px solid black', fontWeight: 400 }}
              >
                Abort
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Blog Content */}
      <article className="md:hidden absolute inset-0 bg-white overflow-y-auto p-6 z-30 custom-scrollbar" id="mobile-blog">
        <div className="flex justify-end items-center mb-6">
          {showBlogContentFrame && (
            <div className="flex gap-2">
              <button
                onClick={prevPost}
                className="w-8 h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors"
                style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}
              >
                <ChevronLeft size={16} className="text-black" />
              </button>
              <button
                onClick={nextPost}
                className="w-8 h-8 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors"
                style={{ fontFamily: 'Courier Prime, Couri?er, monospace', fontWeight: 400 }}
              >
                <ChevronRight size={16} className="text-black" />
              </button>
            </div>
          )}
        </div>

        {currentPostCoverImage && (
          <div className="w-full aspect-[3/4] mb-4 border-2 border-black p-1 bg-white">
            <img
              src={getOptimizedUrl(currentPostCoverImage || '', 1200)}
              alt={`${currentPost.title || 'Blog'} cover`}
              className="w-full h-full object-cover border border-black"
              draggable={false}
            />
          </div>
        )}

        <h1 className="uppercase text-4xl font-bold mb-1" style={{ fontFamily: 'dotmatri, serif' }}>
          {currentPost.title}
        </h1>

        {currentPostIndex !== 1 && currentPost.subtitle && (
          Array.isArray(currentPost.subtitle) ? (
            <div className="mb-4">
              {currentPost.subtitle.map((para, idx) => (
                <h2
                  key={idx}
                  className="mb-4 leading-relaxed text-base"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, color: '#000', lineHeight: 1.3 }}
                >
                  {para}
                </h2>
              ))}
            </div>
          ) : (
            <h2
              className="mb-4 leading-relaxed text-base"
              style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, color: '#000', lineHeight: 1.3 }}
            >
              {currentPost.subtitle}
            </h2>
          )
        )}

        <p
          className={`text-base mb-10 ${(currentPostIndex === 0 || currentPostIndex === 1 || currentPostIndex === 2) ? 'text-gray-500 text-sm md:text-base lg:text-lg leading-snug' : ''}`}
          style={(currentPostIndex === 0 || currentPostIndex === 1 || currentPostIndex === 2) ? {
            fontFamily: 'Courier Prime, Courier, monospace',
            fontWeight: 400,
            color: '#6B7280',
            lineHeight: 1.3,
          } : {
            fontFamily: 'Courier Prime, monospace',
            fontWeight: 400,
          }}
        >
          {currentPost.date} {currentPost.readingTime ? '• ' + currentPost.readingTime + ' min' : '• 5 min'}
        </p>

        {currentPost.previewSnippet && (
          <p
            className="text-black mb-8 text-sm md:text-base lg:text-lg leading-snug"
            style={{
              fontFamily: 'BDO Grotesk, sans-serif',
              fontWeight: 400,
              lineHeight: 1.3,
            }}
          >
            {currentPost.previewSnippet}
          </p>
        )}

        <div className="space-y-6">
          {currentPost.content.map((paragraph, index) => {
            if (paragraph.length > 50) {
              return (
                <p
                  key={index}
                  className="text-black leading-relaxed text-sm md:text-base lg:text-lg"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                >
                  {paragraph}
                </p>
              );
            } else if (paragraph.length > 0) {
              return (
                <h3
                  key={index}
                  className="font-bold text-black mt-8 mb-4 text-base md:text-lg lg:text-xl"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 700 }}
                >
                  {paragraph}
                </h3>
              );
            }
            return null;
          })}
        </div>

        {currentPostIndex === 1 && currentPost.subtitle && (
          <div className="mt-10">
            {Array.isArray(currentPost.subtitle)
              ? currentPost.subtitle.map((para, idx) => (
                <p
                  key={idx}
                  className="text-black leading-relaxed text-sm md:text-base lg:text-lg mb-6"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                >
                  {para}
                </p>
              ))
              : currentPost.subtitle.split('\n\n').map((para, idx) => (
                <p
                  key={idx}
                  className="text-black leading-relaxed text-sm md:text-base lg:text-lg mb-6"
                  style={{ fontFamily: 'BDO Grotesk, sans-serif', fontWeight: 400, lineHeight: 1.3 }}
                >
                  {para}
                </p>
              ))}
          </div>
        )}
      </article>

      {/* Game mode overlay */}
      {gameMode && (
        <>
          {eyes.map((eye) => (
            <div
              key={eye.id}
              className="absolute z-40"
              style={{
                top: eye.y,
                left: eye.x,
                pointerEvents: 'none',
              }}
            >
              <img
                src="https://raw.githubusercontent.com/Raeskaa/studionufab/ec81588c796d013bbd3fcb4d473985d8e0f87a8b/Nufab%20Eye%20White.svg"
                alt="Eye Icon"
                style={{ width: '80.6px', height: '80.6px' }}
              />
            </div>
          ))}
          
          {bullets.map(bullet => (
            <div
              key={bullet.id}
              className="absolute z-50"
              style={{
                left: bullet.x,
                top: bullet.y,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              {bullet.type === 'asterisk' ? (
                <img
                  src="https://raw.githubusercontent.com/Raeskaa/studionufab/refs/heads/main/asterisk.svg"
                  alt="Asterisk Bullet"
                  style={{ width: '20px', height: '20px' }}
                />
              ) : (
                <div
                  style={{
                    width: '8px',
                    height: '24px',
                    background: 'black',
                    borderRadius: '4px',
                  }}
                />
              )}
            </div>
          ))}
          
          <div
            className="fixed z-50"
            style={{
              left: gunX,
              bottom: 0,
              width: '60px',
              height: '60px',
              transform: 'translate(-50%, 0)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <img
              src="https://raw.githubusercontent.com/Raeskaa/studionufab/refs/heads/main/Gun.svg"
              alt="Gun"
              style={{ width: '60px', height: '60px' }}
            />
          </div>
          
          {!gameOver && (
            <div className="fixed top-5 right-5 z-50 bg-white border border-black px-4 py-2 text-black text-lg" style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}>
              {killCount < 20 ? `${killCount}/20` : `${killCount}/20`}
            </div>
          )}
          
          {killCount >= 20 && !gameOver && (
            <button
              onClick={handleExitGame}
              className="fixed bottom-5 left-5 z-50 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-6 py-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace',  border:'1px solid black', fontWeight: 400 }}
            >
              Back
            </button>
          )}
          
          {/* {gameOver && (
            <button
              onClick={handleExitGame}
              className="fixed bottom-5 right-5 z-50 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-6 py-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace',  border:'1px solid black', fontWeight: 400 }}
            >
              Back to blog
            </button>
          )} */}
          
          {gameOver && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-none shadow-lg text-center border-2 border-black">
                <h3 className="text-lg font-bold mb-4 text-black" style={{ fontFamily: 'Courier Prime, Courier, monospace', fontWeight: 400 }}>
                  nazar lag gayi
                </h3>
                <button
                  onClick={handleExitGame}
                  className="h-8 bg-white hover:bg-gray-100 border border-black px-4 transition-colors active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
                  style={{ fontFamily: 'Courier Prime, Courier, monospace',   border:'1px solid black', fontWeight: 400 }}
                >
                  Reload
                </button>
              </div>
               {gameOver && (
            <button
              onClick={handleExitGame}
              className="fixed bottom-5 left-5 z-50 bg-white hover:bg-gray-100 border border-black flex items-center justify-center transition-colors px-6 py-2 active:scale-95 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-black"
              style={{ fontFamily: 'Courier Prime, Courier, monospace',  border:'1px solid black', fontWeight: 400 }}
            >
              Back
            </button>
          )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NewBlog;
